'use client';

import { cn } from '@core/utils';
import * as RadixDropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import {
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import SvgCheck from '../../../__generated__/icon/SvgCheck';
import SvgChevrondown from '../../../__generated__/icon/SvgChevrondown';
import {
  checkboxBoxVariants,
  CheckboxMark,
} from '../../atom/Checkbox/checkboxVisuals';
import type { InputProps } from '../../atom/Input/Input';
import { Input } from '../../atom/Input/Input';
import type { DropdownMenuType } from './type';

const FIRST_ENABLED_ITEM = '[role^="menuitem"]:not([data-disabled])';
const focusOnMount = (node: HTMLElement | null) => node?.focus();
const revealOnMount = (node: HTMLElement | null) =>
  node?.scrollIntoView({ block: 'nearest' });

/**
 * * 드롭다운(셀렉트박스) 목록 요소 타입
 */
interface DropdownMenuItem<T extends string | number> {
  label: string;
  value: T;
}

/**
 * * 드롭다운(셀렉트박스) 그룹 타입. 섹션 라벨과 해당 섹션의 옵션 목록을 가짐.
 */
export interface DropdownMenuGroup<T extends string | number> {
  label: string;
  options: DropdownMenuItem<T>[];
}

/**
 * * 드롭다운(셀렉트박스) 컴포넌트 타입
 */
interface DropdownMenuProps<
  T extends string | number,
  M extends boolean = false,
  Type extends DropdownMenuType = 'basic',
> extends Omit<
  InputProps,
  | 'rightContent'
  | 'type'
  | 'leftContent'
  | 'onClear'
  | 'onChange'
  | 'value'
  | 'onFocus'
  | 'onBlur'
  | 'onInvalid'
> {
  /** 드롭다운 메뉴 아이템 목록 (flat). groups가 주어지면 무시됨. */
  options?: DropdownMenuItem<T>[];
  /** 그룹으로 묶인 옵션 목록. 주어지면 options 대신 섹션 라벨과 함께 렌더링됨. */
  groups?: DropdownMenuGroup<T>[];
  /** 열림/닫힌 상태 외부 제어용 */
  open?: boolean;
  /** 열림/닫힌 상태 외부 제어용 */
  onOpenChange?: (opened: boolean) => void;
  /** 다중 선택 여부 */
  multiple?: M;
  /** 선택 값 변경 핸들러 */
  onChange?: (value: M extends true ? T[] : T) => void;
  /** 선택 값 */
  value?: M extends true ? T[] : T;
  /** 드롭다운 트리거 타입. 'custom'이면 children을 트리거로 렌더링 */
  type?: Type;
  /** 트리거 크기 설정 (type이 'text' 또는 'custom'인 경우 사용 불가) */
  dimensions?: Type extends 'text' | 'custom'
    ? never
    : InputProps['dimensions'];
  /** type='custom'일 때 트리거 내부에 렌더링할 노드 */
  children?: ReactNode;
  /** 드롭다운 메뉴 위치 (top, bottom, left, right) */
  side?: 'top' | 'bottom' | 'left' | 'right';
  /**
   * 드롭다운 메뉴 정렬 (start, center, end)
   * TODO: end를 넣었을 경우 사파리 브라우저, 네이버 브라우저에서 정상적으로 동작하지 않음. 'start'처럼 동작함. 이 경우에 contentClassName에 min-width를 import로 넣어줘야 함.
   */
  align?: 'start' | 'center' | 'end';
  /** 드롭다운 컨텐츠 클래스명 */
  contentClassName?: string;
  /** 드롭다운 메뉴 아이템 비활성화 여부 */
  itemDisabled?: (item: DropdownMenuItem<T>) => boolean;
  /** 검색 기능 활성화 여부 (드롭다운 내부에 검색 input 표시) */
  searchable?: boolean;
  /** 검색 placeholder */
  searchPlaceholder?: string;
}

/**
 * * 드롭다운(셀렉트박스) DS 컴포넌트
 * @summary 디자인 시스템의 드롭다운 컴포넌트입니다.
 * @description DS의 label 및 error, validation message 등등에 대한 처리는 InputDecorator 컴포넌트로 감싸서 사용해주세요
 */
export const DropdownMenu = <
  T extends string | number,
  M extends boolean = false,
  Type extends DropdownMenuType = 'basic',
>({
  options = [],
  groups,
  value,
  placeholder,
  onChange,
  invalid,
  disabled,
  dimensions,
  open,
  onOpenChange,
  multiple,
  className,
  type = 'basic' as Type,
  contentClassName,
  side = 'bottom',
  align = 'start',
  itemDisabled,
  defaultValue,
  searchable = false,
  searchPlaceholder = '검색',
  children,
}: DropdownMenuProps<T, M, Type>) => {
  const [internalValue, setInternalValue] =
    useState<M extends true ? T[] : T>();
  const [internalOpen, setInternalOpen] = useState(open ?? false);
  const [searchQuery, setSearchQuery] = useState('');
  const triggerRef = useRef<HTMLDivElement>(null);
  const [triggerWidth, setTriggerWidth] = useState(0);
  const contentId = useId();

  // groups가 주어지면 groups의 옵션들을 flatten해서 내부 로직에 사용. 아니면 기존 options 사용.
  const flatOptions: DropdownMenuItem<T>[] =
    groups?.flatMap((g) => g.options) ?? options ?? [];

  const currentValue = value ?? internalValue ?? defaultValue;
  const displayValue = Array.isArray(currentValue)
    ? currentValue
        .map((v) => flatOptions.find((opt) => opt.value === v)?.label ?? v)
        .join(', ')
    : (flatOptions.find((opt) => opt.value === currentValue)?.label ??
      currentValue ??
      '');

  // 검색어로 필터링하는 공통 헬퍼
  const applySearchFilter = (items: DropdownMenuItem<T>[]) =>
    searchable && searchQuery
      ? items.filter((item) =>
          item.label.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : items;

  // 검색어로 필터링된 옵션 (flat 모드에서 사용)
  const filteredOptions = applySearchFilter(flatOptions);

  // groups 모드에서 검색 필터링한 후 빈 그룹은 제거
  const filteredGroups = groups
    ?.map((g) => ({ ...g, options: applySearchFilter(g.options) }))
    .filter((g) => g.options.length > 0);

  // 빈 결과 여부 (flat/groups 공통)
  const isEmptyResult = groups
    ? (filteredGroups?.length ?? 0) === 0
    : filteredOptions.length === 0;

  const renderItem = (item: DropdownMenuItem<T>, index: number) => {
    const isSelected = Array.isArray(currentValue)
      ? currentValue.includes(item.value)
      : currentValue === item.value;
    const isDisabled = itemDisabled?.(item);

    if (multiple) {
      return (
        <RadixDropdownMenuPrimitive.CheckboxItem
          key={`${String(item.value)}-${index}`}
          checked={isSelected}
          disabled={isDisabled}
          className={cn(
            'flex w-full select-none items-center gap-4 overflow-hidden px-16 py-8 outline-none transition-colors duration-150 hover:bg-B-50 active:bg-B-100 data-[highlighted]:bg-B-50',
            {
              'bg-Gray-100 opacity-50 cursor-not-allowed': isDisabled,
            },
          )}
          onSelect={(e) => {
            e.preventDefault();
            handleClick(item.value);
          }}
        >
          <span
            aria-hidden
            className={cn(
              checkboxBoxVariants({
                checked: isSelected,
                disabled: isDisabled,
              }),
            )}
          >
            <CheckboxMark
              className={cn(
                'transition-opacity duration-150 motion-reduce:transition-none',
                isSelected ? 'opacity-100' : 'opacity-0',
              )}
            />
          </span>
          <p className="line-clamp-1 flex-1 text-left font-400 text-Gray-800 typo-body4">
            {item.label}
          </p>
        </RadixDropdownMenuPrimitive.CheckboxItem>
      );
    }

    return (
      <RadixDropdownMenuPrimitive.Item
        key={`${String(item.value)}-${index}`}
        disabled={isDisabled}
        ref={
          isSelected ? (searchable ? revealOnMount : focusOnMount) : undefined
        }
        className={cn(
          'flex w-full select-none items-center justify-between gap-8 overflow-hidden px-16 py-8 outline-none transition-colors duration-150 hover:bg-B-50 active:bg-B-100 data-[highlighted]:bg-B-50',
          {
            'bg-Gray-100 opacity-50 cursor-not-allowed': isDisabled,
          },
        )}
        onSelect={() => handleClick(item.value)}
      >
        <p className="line-clamp-1 flex-1 text-left font-400 text-Gray-800 typo-body4">
          {item.label}
        </p>
        {isSelected && <SvgCheck className="size-16 text-B-500" />}
      </RadixDropdownMenuPrimitive.Item>
    );
  };

  const handleClick = (clickedValue: T) => {
    if (multiple) {
      const current = (
        Array.isArray(value)
          ? value
          : Array.isArray(internalValue)
            ? internalValue
            : []
      ) as T[];
      const newValue = current.includes(clickedValue)
        ? current.filter((v) => v !== clickedValue)
        : [...current, clickedValue];

      setInternalValue(newValue as M extends true ? T[] : T);
      onChange?.(newValue as M extends true ? T[] : T);
    } else {
      setInternalValue(clickedValue as M extends true ? T[] : T);
      onChange?.(clickedValue as M extends true ? T[] : T);
    }
  };

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  useLayoutEffect(() => {
    const measureWidth = () => {
      if (triggerRef.current) {
        setTriggerWidth(triggerRef.current.offsetWidth);
      }
    };

    measureWidth();

    const resizeObserver = new ResizeObserver(measureWidth);
    if (triggerRef.current) {
      resizeObserver.observe(triggerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [displayValue]);

  const isOpen = open ?? internalOpen;

  return (
    <RadixDropdownMenuPrimitive.Root
      open={isOpen}
      onOpenChange={(opened) => {
        onOpenChange?.(opened);
        setInternalOpen(opened);

        if (!opened) {
          setSearchQuery('');
        }
      }}
    >
      <RadixDropdownMenuPrimitive.Trigger asChild disabled={disabled}>
        <div
          ref={triggerRef}
          role="combobox"
          aria-controls={contentId}
          aria-expanded={isOpen}
          tabIndex={disabled ? -1 : 0}
          className={cn(
            'relative h-fit cursor-pointer select-none overflow-hidden outline-none',
            {
              'min-w-130 lg:min-w-150': type === 'basic',
              'w-fit min-w-[unset] lg:w-fit lg:min-w-[unset]':
                type === 'text' || type === 'custom',
              'cursor-not-allowed': disabled,
            },
            className,
          )}
          onClick={(e) => {
            e.preventDefault();
            if (!disabled) {
              setInternalOpen(!isOpen);
              onOpenChange?.(!isOpen);
            }
          }}
          onPointerDown={(e) => {
            e.preventDefault();
          }}
        >
          {type === 'basic' ? (
            <Input
              rightContent={
                <SvgChevrondown
                  className={cn(
                    'size-16 text-Gray-600 transition-transform duration-300 motion-reduce:transition-none',
                    {
                      'size-20': dimensions === 'H56',
                      'text-Gray-500': disabled,
                      '-rotate-180 text-Gray-600': isOpen,
                    },
                  )}
                />
              }
              value={displayValue}
              placeholder={placeholder}
              className={cn({ 'w-full border-1 border-Gray-900': isOpen })}
              invalid={invalid}
              type="button"
              disabled={disabled}
              dimensions={dimensions}
              tabIndex={-1}
            />
          ) : type === 'custom' ? (
            children
          ) : (
            <div className="flex items-center justify-between gap-4 overflow-hidden py-10 pl-4 pr-2">
              <p
                className={cn(
                  'flex-1 truncate text-left font-400 text-Gray-900 typo-body4',
                  { 'text-Gray-400': disabled },
                )}
              >
                {displayValue || placeholder}
              </p>
              <SvgChevrondown
                className={cn(
                  'size-12 text-Gray-900 transition-transform duration-300 motion-reduce:transition-none',
                  {
                    '-rotate-180 text-Gray-600': isOpen,
                    'text-Gray-600': displayValue,
                    'text-Gray-400': disabled,
                  },
                )}
              />
            </div>
          )}
        </div>
      </RadixDropdownMenuPrimitive.Trigger>
      <RadixDropdownMenuPrimitive.Portal>
        <RadixDropdownMenuPrimitive.Content
          id={contentId}
          onKeyDown={(event) => {
            if (
              searchable &&
              event.key === 'ArrowUp' &&
              event.target ===
                event.currentTarget.querySelector(FIRST_ENABLED_ITEM)
            ) {
              event.preventDefault();
              event.currentTarget.querySelector('input')?.focus();
            }
          }}
          className={cn(
            'max-h-260 w-full min-w-130 overflow-y-auto rounded-xsmall bg-Gray-white py-12 shadow-xl hide-scrollbar lg:min-w-150',
            contentClassName,
          )}
          side={side}
          align={align}
          sideOffset={8}
          sticky="always"
          loop={false}
          style={{
            width: triggerWidth,
            minWidth:
              type === 'text' || type === 'custom' ? 'max-content' : undefined,
            zIndex: 20,
          }}
        >
          {searchable && (
            <div
              className="sticky -top-12 z-10 -mt-12 bg-Gray-white px-12 pb-8 pt-12"
              onPointerDown={(e) => {
                e.stopPropagation();
              }}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  document
                    .getElementById(contentId)
                    ?.querySelector<HTMLElement>(FIRST_ENABLED_ITEM)
                    ?.focus();
                } else if (event.key.length === 1) {
                  // Radix Menu typeahead가 input 안 글자 키에도 반응해 포커스를 아이템으로 옮긴다
                  event.stopPropagation();
                }
              }}
            >
              <Input
                ref={focusOnMount}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={searchPlaceholder}
                dimensions="H40"
                className="w-full"
              />
            </div>
          )}
          {isEmptyResult && searchable && searchQuery ? (
            <div className="px-16 py-12 text-center text-Gray-500 typo-body4">
              검색 결과가 없습니다
            </div>
          ) : groups ? (
            filteredGroups?.map((group) => (
              <RadixDropdownMenuPrimitive.Group key={group.label}>
                <RadixDropdownMenuPrimitive.Label className="px-16 pb-4 pt-8 font-500 text-Gray-550 typo-caption1">
                  {group.label}
                </RadixDropdownMenuPrimitive.Label>
                {group.options.map((item, index) => renderItem(item, index))}
              </RadixDropdownMenuPrimitive.Group>
            ))
          ) : (
            filteredOptions.map((item, index) => renderItem(item, index))
          )}
        </RadixDropdownMenuPrimitive.Content>
      </RadixDropdownMenuPrimitive.Portal>
    </RadixDropdownMenuPrimitive.Root>
  );
};
